import React, { useState, useEffect } from "react";
import { useSnackBar } from "../../../../hooks/useSnackBar";

// components imports
import TabsComponent from "../../../../Components/Common/TabsComponent";
import ModalWrapper from "../../../../Components/Modals/ModalWrapper";

// apis imports
import { fetchProductHistory } from "../../../../services/product.service";
// library imports
import HistoryTable from "../../../../Components/BoltTable/BoltTable";
import { FaArrowAltCircleUp, FaArrowAltCircleDown } from "react-icons/fa";
import DetailsModal from "../../../Warehouse/WarehouseOrders/Components/OrderDetails/DetailsModal";
import Tooltip from "../../../../Components/Tooltip/Tooltip";
import GRNDetailsModal from "../../GRN/Components/GRNDetailsModal/GRNDetailsModal";
import { convertTime, humanizeCamelCase } from "../../../../helpers/helpers";
import { useSelector } from "react-redux";
import "./HistoryModal.css";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

export default function HistoryModal({
  open,
  setOpen,
  setSnack: setSnackProp,
}) {
  const { t } = useTranslation(["inventory", "common"]);
  // Several callers don't pass setSnack; without the fallback the error path throws.
  const notify = useSnackBar();
  const setSnack = setSnackProp || notify;
  const user = useSelector((state) => state.user);
  const [data, setData] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [warehouseTabs, setWarehouseTabs] = useState([]);
  const [refresh, setRefresh] = useState(0);

  const handleClose = () => {
    setOpen(false);
    setData({});
    setorderModal(false);
    setActiveTab(0);
    setRefresh(0);
  };
  const [orderModal, setorderModal] = useState(false);
  const [grnModal, setGRNModal] = useState(false);

  const regex = /\(([^)]+)\)/;

  // Create warehouse tabs dynamically
  useEffect(() => {
    const list = Array.isArray(user?.user_approved_warehouses)
      ? user.user_approved_warehouses
      : [];

    if (list.length > 0) {
      const tabs = [{ label: i18n.t("inventory:productModals.all"), value: 0, warehouseId: null }];

      list.forEach((warehouse, index) => {
        const label = warehouse?.warehouse?.name ?? i18n.t("inventory:productModals.unknown");
        const id = warehouse?.warehouse?.id ?? null;
        tabs.push({ label, value: index + 1, warehouseId: id });
      });

      setWarehouseTabs(tabs);
    } else {
      setWarehouseTabs([{ label: i18n.t("inventory:productModals.all"), value: 0, warehouseId: null }]);
    }
  }, [user?.user_approved_warehouses]);

  useEffect(() => {
    const valid = Array.isArray(warehouseTabs) && warehouseTabs.length > 0;
    if (!valid) {
      if (activeTab !== 0) setActiveTab(0);
      return;
    }
    const exists = warehouseTabs.some((t) => t?.value === activeTab);
    if (!exists) setActiveTab(0);
  }, [warehouseTabs, activeTab]);

  useEffect(() => {
    if (open && warehouseTabs?.length > 0) {
      setData({});
      setRefresh((prev) => prev + 1);
    }
  }, [activeTab, open]);

  const columns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "130px",
    },
    {
      id: 1,
      label: t("productModals.customer"),
      field_name: "payment_type",
      render: (row) => (
        <>{row?.order?.order_shipping_address_details?.first_name ?? "-"}</>
      ),
      width: "200px",
      fixed: "left",
    },
    {
      id: 2,
      label: t("productModals.type"),
      field_name: "Type",
      render: (row) => <>{humanizeCamelCase(row?.actions?.type)}</>,
      width: "200px",
    },
    {
      id: 3,
      label: t("productModals.difference"),
      field_name: "amount",
      render: (row) => (
        <div className="flex justify-center">
          {parseInt(row?.actions?.afterUpdationQuantity, 10) -
            parseInt(row?.actions?.beforeUpdationQuantity, 10) ?? "-"}
          {parseInt(row?.actions?.beforeUpdationQuantity, 10) >
          parseInt(row?.actions?.afterUpdationQuantity, 10) ? (
            <FaArrowAltCircleDown className="mt-0.5 ml-1 text-state-danger-text" />
          ) : (
            <FaArrowAltCircleUp className="mt-0.5 ml-1 text-state-success-text" />
          )}
        </div>
      ),
      width: "200px",
    },
    {
      id: 4,
      label: t("productModals.newQty"),
      field_name: "balance",
      render: (row) => <>{row?.actions?.afterUpdationQuantity ?? "-"}</>,
      width: "200px",
    },
    {
      id: 5,
      label: t("productModals.description"),
      field_name: "description",
      render: (row) => (
        <>{row.description?.replace(regex, "")?.trim() ?? "-"}</>
      ),
      width: "200px",
    },
    {
      id: 5,
      label: t("productModals.notes"),
      field_name: "note",
      render: (row) => <>{row.description?.match(regex)?.[1] ?? "-"}</>,
      width: "200px",
    },

    {
      id: 6,
      label: t("productModals.preview"),
      field_name: "Preview",
      render: (row) => (
        <>
          {row?.order?.order_id && (
            <Tooltip title={t("productModals.clickToViewOrder")}>
              <div
                onClick={() => {
                  setorderModal([row?.order?.id, false]);
                }}
                className="cursor-pointer text-brand underline"
              >
                {row?.order?.order_id}
              </div>
            </Tooltip>
          )}
          {row?.traceable?.grn_no && (
            <Tooltip title={t("productModals.clickToViewStockList")}>
              <div
                onClick={() => {
                  setGRNModal(row?.traceable);
                }}
                className="cursor-pointer text-brand underline"
              >
                {row?.traceable?.grn_no}
              </div>
            </Tooltip>
          )}
        </>
      ),
      width: "200px",
    },
    {
      id: 7,
      label: t("productModals.warehouse"),
      field_name: "warehouse",
      render: (row) => <>{row?.warehouse?.name ?? "-"}</>,
      width: "200px",
    },
    {
      id: 8,
      label: t("productModals.createdBy"),
      field_name: "Created By",
      render: (row) => <>{row?.user?.username ?? "-"}</>,
      width: "200px",
    },
    {
      id: 9,
      label: t("productModals.createdAt"),
      render: (row) => <>{convertTime(row?.created_at)}</>,
      width: "200px",
      fixed: "right",
    },
  ];

  const getProductHistory = async (page, perPage, filter) => {
    const tabsSafe =
      Array.isArray(warehouseTabs) && warehouseTabs.length > 0
        ? warehouseTabs
        : [{ label: t("productModals.all"), value: 0, warehouseId: null }];
    const selectedTab =
      tabsSafe.find((tab) => tab?.value === activeTab) || tabsSafe[0];
    const warehouseId = selectedTab?.warehouseId ?? null;

    let response = await fetchProductHistory(
      open?.row?.id,
      page,
      perPage,
      "",
      warehouseId
    );
    if (!response.error) {
      setData(response?.data?.result);
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };

  return (
    <>
      <DetailsModal
        open={orderModal}
        setOpen={setorderModal}
        setSnack={setSnack}
      />
      <GRNDetailsModal
        open={grnModal}
        setOpen={setGRNModal}
        setSnack={setSnack}
      />
      <ModalWrapper
        isOpen={open}
        onClose={handleClose}
        boxClassName="lg:min-w-[1200px] !w-fit !max-w-[80vw] h-[90vh] max-h-[1000px] overflow-hidden p-0 m-2 !border-none"
        backdropClassName="bg-black bg-opacity-50"
      >
        <div className="bg-ui-surface rounded-xl w-full flex flex-col overflow-hidden h-full">
          {/* Header Section */}
          <div className="flex-shrink-0 px-6 pt-3 border-b border-soft-gray">
            <div className="flex items-center justify-center relative">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-ui-text">
                  {t("productModals.productsHistory")}
                </h2>
                <p className="text-sm text-ui-text-muted mt-1">
                  {open?.row?.full_name ?? ""} ({open?.row?.code ?? ""})
                </p>
              </div>
              <button
                onClick={handleClose}
                className="absolute right-0 p-2 hover:bg-ui-surface-sunken rounded-full transition-colors"
                title={t("common:actions.close")}
              >
                <svg
                  className="w-5 h-5 text-ui-text-muted"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Warehouse Tabs Section */}
          {warehouseTabs?.length > 1 && (
            <div className="flex-shrink-0 px-6 pt-2">
              <div className="flex justify-center">
                <TabsComponent
                  items={warehouseTabs}
                  tab={activeTab}
                  setTab={setActiveTab}
                />
              </div>
            </div>
          )}

          {/* Table Content Section */}
          <div className="flex-1 overflow-hidden p-4 h-full">
            <div className="h-full">
              {refresh > 0 && (
                <HistoryTable
                  tableId="historyModalTableForProdcut"
                  message={t("productModals.noProductHistoryAvailableToDisplay")}
                  searchPlaceHolder={t("productModals.searchNotAvailable")}
                  getAllData={getProductHistory}
                  data={data}
                  columns={columns}
                  disableAutoResizing={true}
                  maxHeight="100%"
                  refresh={refresh}
                />
              )}
            </div>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
}
